// Экран 4 из ТЗ раздел 3.3 + 3.4.5: список привязанных устройств, привязка нового (с показом
// ссылок на установку), отвязка. Основное устройство подписки (Device.is_primary) отвязать нельзя
// — см. app/api/routes/miniapp/devices.py, кнопка для него не показывается (backend это тоже
// проверяет сам, фронт — только UX).
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  attachDevice,
  detachDevice,
  fetchDeviceInstallPackages,
  fetchSubscription,
  type InstallPackage,
} from "../api/client";
import { useSubscription } from "../hooks/useSubscription";

export function DevicesScreen() {
  const { t } = useTranslation();
  const { subscription, loading, error, setSubscription, setError } = useSubscription();
  const [name, setName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [newPackages, setNewPackages] = useState<InstallPackage[] | null>(null);
  const [packagesByDevice, setPackagesByDevice] = useState<Record<number, InstallPackage[]>>({});
  const [busyDeviceId, setBusyDeviceId] = useState<number | null>(null);

  const refresh = () =>
    fetchSubscription()
      .then(setSubscription)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));

  const handleAttach = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setAttaching(true);
    setError(null);
    setNewPackages(null);
    attachDevice(trimmed)
      .then(({ install_packages }) => {
        setName("");
        setNewPackages(install_packages);
        return refresh();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setAttaching(false));
  };

  const handleShowLinks = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    fetchDeviceInstallPackages(deviceId)
      .then((packages) => setPackagesByDevice((prev) => ({ ...prev, [deviceId]: packages })))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const handleDetach = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    setError(null);
    detachDevice(deviceId)
      .then(() => refresh())
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const renderPackages = (packages: InstallPackage[]) => (
    <ul>
      {packages.length === 0 && <li>{t("devices_links_pending")}</li>}
      {packages.map((pkg) => (
        <li key={pkg.os}>
          <a href={pkg.url}>{pkg.os}</a>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <h1>{t("devices_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {loading && <p>{t("loading")}</p>}
      {!loading && subscription === null && <p>{t("subscription_none")}</p>}
      {subscription && (
        <>
          {subscription.devices.length === 0 && <p>{t("devices_empty")}</p>}
          <ul>
            {subscription.devices.map((device) => (
              <li key={device.id}>
                {device.name}
                {device.os ? ` (${device.os})` : ""}
                {device.is_primary ? ` — ${t("devices_primary")}` : ""}{" "}
                <button
                  type="button"
                  onClick={() => handleShowLinks(device.id)}
                  disabled={busyDeviceId === device.id}
                >
                  {t("devices_show_links")}
                </button>{" "}
                {!device.is_primary && (
                  <button
                    type="button"
                    onClick={() => handleDetach(device.id)}
                    disabled={busyDeviceId === device.id}
                  >
                    {t("devices_detach")}
                  </button>
                )}
                {packagesByDevice[device.id] && renderPackages(packagesByDevice[device.id])}
              </li>
            ))}
          </ul>

          {subscription.devices.length < subscription.plan.device_limit ? (
            <form onSubmit={handleAttach}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("devices_name_placeholder")}
              />
              <button type="submit" disabled={attaching || !name.trim()}>
                {attaching ? t("loading") : t("devices_attach")}
              </button>
            </form>
          ) : (
            <p>{t("devices_limit_reached")}</p>
          )}

          {newPackages && (
            <div>
              <p>{t("devices_new_links_title")}</p>
              {renderPackages(newPackages)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
